import React, {useState, useRef, useEffect} from 'react';
import {permColors} from '../../utils/colors';
import {RightOutlined} from '@ant-design/icons';

const steps = [
	{
		id: 1,
		title: 'Post a Job',
		content: 'List your role in minutes - no credit card needed.',
		src: '/images/how-it-works-section/post-job.png',
		alt: 'Initial Consultation Illustration',
	},
	{
		id: 2,
		title: 'Talent applies to your ad',
		content: 'Top Filipino candidates apply directly to your inbox.',
		src: '/images/ai-images/applicant-page-ai.png',
		alt: 'Compliance Review Illustration',
	},
	{
		id: 3,
		title: 'Review applicants and connect',
		content: 'Message, interview, and hire your best fit.',
		src: '/images/ai-images/applicant-page-ai.png',
		alt: 'Contract Preparation Process',
	},
];

export default function HowItWorksSection() {
	const [activeStep, setActiveStep] = useState(1);
	const refs = useRef(steps.map(() => React.createRef()));
	const sidebarRef = useRef(null);

	const handleStepClick = (stepId: any) => {
		const yOffset = -26; // 100px for navbar + 20px extra padding
		const element = refs.current[stepId - 1].current;
		const y = (element as HTMLElement).getBoundingClientRect().top + window.pageYOffset + yOffset;
		window.scrollTo({top: y, behavior: 'smooth'});
	};

	useEffect(() => {
		const observerOptions = {
			root: null,
			rootMargin: '-50% 0px -50% 0px', // Consider the middle 50% of the viewport
			threshold: 0,
		};

		const observerCallback = (entries: any) => {
			entries.forEach((entry: any) => {
				if (entry.isIntersecting) {
					const stepId = parseInt(entry.target.getAttribute('data-step-id'));
					setActiveStep(stepId);
				}
			});
		};

		const observer = new IntersectionObserver(observerCallback, observerOptions);

		refs.current.forEach((ref, index) => {
			if (ref.current) {
				observer.observe(ref.current as any);
			}
		});

		return () => {
			refs.current.forEach((ref) => {
				if (ref.current) {
					observer.unobserve(ref.current as any);
				}
			});
		};
	}, []);

	return (
		<div className="bg-white">
			<div className="container mx-auto px-4 py-10 pt-20">
				<h3 className="text-lg md:text-2xl text-center font-normal mb-10 text-darkCharcoal" id="how-it-works-header">
					What does the Permworks<span className="font-semibold"> Posting a Job Ad</span> process look like?
				</h3>
				<div className="flex flex-col md:flex-row">
					<div
						ref={sidebarRef}
						className="w-full flex flex-col gap-2 md:w-1/4 md:sticky md:self-start p-4 mb-8 md:mb-0 md:mr-6  pt- pb-0 border-gray-200"
						style={{top: '40px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto'}}>
						{steps.map((step) => (
							<div
								key={step.id}
								className={`flex items-center font-light cursor-pointer transition-all mb-4 ${activeStep === step.id ? 'text-antBlue font-medium' : 'text-charcoal'}`}
								onClick={() => handleStepClick(step.id)}>
								<div className={`w-7 h-7 rounded-full flex items-center text-sm justify-center mr-2 ${activeStep === step.id ? 'bg-antBlue text-white' : 'bg-backgroundGrey'}`}>
									{step.id}
								</div>
								<span className="text-sm">{step.title}</span>
								{activeStep === step.id && <RightOutlined className="ml-auto" />}
							</div>
						))}
					</div>
					<div className={'w-full md:w-3/4'}>
						{steps.map((step) => (
							<div key={step.id} ref={refs.current[step.id - 1] as React.RefObject<HTMLDivElement>} data-step-id={step.id} className={'p-6 mb-8 ' + (step.id !== 9 ? ' pb-20' : '')}>
								<h4 className="text-xl font-medium  mb-1 text-darkCharcoal">
									<span>{step.id}</span>. {step.title}
								</h4>
								<div className=" mb-4 text-sm font-light" style={{color: permColors.charcoal}}>
									{step.content}
								</div>
								<img src={step.src} alt={step.alt} className="p-2 w-full rounded-md shadow-border-no-background object-contain md:max-w-[800px]" />
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}
